import type { CompanionDecisionAction } from "./decision";
import type { CompanionAvailabilityWindow } from "./availability-fit";

export type CompanionManualAvailabilityKind =
  | "day-window"
  | "after-work"
  | "travel-time"
  | "free-text";

export type CompanionManualAvailability = {
  label: string;
  kind: CompanionManualAvailabilityKind;
  dayLabel: string | null;
  timeOfDay: "morning" | "afternoon" | "evening" | null;
  durationMinutes: number | null;
  travelMinutes: number | null;
  sourceText: string;
};

export type CompanionAvailabilityParseResult =
  | { ok: true; availability: CompanionManualAvailability }
  | { ok: false; error: string };

export type CompanionAvailabilityDecision = {
  action: CompanionDecisionAction;
  score: number | null;
};

export type CompanionAvailabilityDecisionFit = {
  priority: "drop-everything" | "possible" | "watch" | "not-worth-it";
  score: number;
  label: string;
};

export type CompanionAvailabilityRowInput = {
  user_id: string;
  label: string;
  kind: CompanionManualAvailabilityKind;
  day_label: string | null;
  time_of_day: CompanionManualAvailability["timeOfDay"];
  duration_minutes: number | null;
  travel_minutes: number | null;
  source_text: string;
  active: boolean;
};

const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const TIME_OF_DAY = ["morning", "afternoon", "evening"] as const;
const MIN_LABEL_LENGTH = 2;
const MAX_LABEL_LENGTH = 160;
const MAX_SOURCE_TEXT_LENGTH = 240;

export function parseCompanionAvailability(raw: string): CompanionAvailabilityParseResult {
  const sourceText = raw.trim().replace(/\s+/g, " ");
  if (!sourceText) return { ok: false, error: "Add when you could actually go." };
  if (sourceText.length < MIN_LABEL_LENGTH) {
    return { ok: false, error: "Use at least 2 characters for free time." };
  }
  if (sourceText.length > MAX_SOURCE_TEXT_LENGTH) {
    return { ok: false, error: "Keep free time under 240 characters." };
  }

  const lower = sourceText.toLowerCase();
  const travelMinutes = parseTravelMinutes(lower);
  if (travelMinutes !== null) {
    return availabilityResult({
        label: `Can travel ${durationText(travelMinutes)}`,
        kind: "travel-time",
        dayLabel: null,
        timeOfDay: null,
        durationMinutes: null,
        travelMinutes,
        sourceText,
    });
  }

  if (lower.includes("after work")) {
    const durationMinutes = parseDurationMinutes(lower);
    return availabilityResult({
        label: durationMinutes === null ? "After work" : `After work for ${durationText(durationMinutes)}`,
        kind: "after-work",
        dayLabel: null,
        timeOfDay: null,
        durationMinutes,
        travelMinutes: null,
        sourceText,
    });
  }

  const dayLabel = DAYS.find((day) => lower.includes(day.toLowerCase())) ?? null;
  const timeOfDay = TIME_OF_DAY.find((time) => lower.includes(time)) ?? null;
  if (dayLabel || timeOfDay) {
    const durationMinutes = parseDurationMinutes(lower);
    return availabilityResult({
        label: [dayLabel, timeOfDay].filter(Boolean).join(" "),
        kind: "day-window",
        dayLabel,
        timeOfDay,
        durationMinutes,
        travelMinutes: null,
        sourceText,
    });
  }

  return availabilityResult({
      label: sentenceCase(sourceText),
      kind: "free-text",
      dayLabel: null,
      timeOfDay: null,
      durationMinutes: null,
      travelMinutes: null,
      sourceText,
  });
}

export function scoreCompanionAvailabilityDecision(
  availability: CompanionManualAvailability,
  decision: CompanionAvailabilityDecision,
): CompanionAvailabilityDecisionFit {
  const actionWeight: Record<CompanionDecisionAction, number> = {
    "go-now": 0.96,
    "go-later": 0.66,
    "go-elsewhere": 0.52,
    "do-not-bother": 0.25,
  };
  const availabilityWeight: Record<CompanionManualAvailabilityKind, number> = {
    "after-work": 0.88,
    "day-window": 0.82,
    "travel-time": 0.68,
    "free-text": 0.62,
  };
  const scoreBonus = decision.score !== null && decision.score >= 7.5 ? 0.02 : 0;
  const score = round2(
    Math.min(1, actionWeight[decision.action] * availabilityWeight[availability.kind] + scoreBonus),
  );

  if (score >= 0.8) {
    return {
      priority: "drop-everything",
      score,
      label: "Worth moving for this window",
    };
  }
  if (score >= 0.5) {
    return {
      priority: "possible",
      score,
      label: "Window saved for a realistic plan",
    };
  }
  if (score >= 0.3) {
    return {
      priority: "watch",
      score,
      label: "Keep watching this window",
    };
  }
  return {
    priority: "not-worth-it",
    score,
    label: "Free time saved, but the signal is weak",
  };
}

export function buildCompanionAvailabilityRow(
  userId: string,
  availability: CompanionManualAvailability,
): CompanionAvailabilityRowInput {
  return {
    user_id: userId,
    label: availability.label,
    kind: availability.kind,
    day_label: availability.dayLabel,
    time_of_day: availability.timeOfDay,
    duration_minutes: availability.durationMinutes,
    travel_minutes: availability.travelMinutes,
    source_text: availability.sourceText,
    active: true,
  };
}

export function buildCompanionAvailabilityPreviewWindow(
  availability: CompanionManualAvailability,
): CompanionAvailabilityWindow {
  return {
    id: null,
    label: availability.label,
    kind: availability.kind,
    dayLabel: availability.dayLabel,
    timeOfDay: availability.timeOfDay,
    durationMinutes: availability.durationMinutes,
    travelMinutes: availability.travelMinutes,
    sourceText: availability.sourceText,
    active: true,
    createdAt: null,
    updatedAt: null,
  };
}

function parseTravelMinutes(lower: string): number | null {
  const match = lower.match(/travel\s+(\d{1,3})\s*(minutes?|mins?|hours?|hrs?)/);
  if (!match) return null;
  return clampMinutes(durationMatchToMinutes(match[1], match[2]));
}

function parseDurationMinutes(lower: string): number | null {
  const match = lower.match(/(?:for\s+)?(\d{1,2})\s*(hours?|hrs?|minutes?|mins?)/);
  if (!match) return null;
  return clampMinutes(durationMatchToMinutes(match[1], match[2]));
}

function durationMatchToMinutes(amountText: string | undefined, unit: string | undefined): number {
  const amount = Number(amountText);
  if (!Number.isFinite(amount) || !unit) return 0;
  return unit.startsWith("hour") || unit.startsWith("hr") ? amount * 60 : amount;
}

function clampMinutes(minutes: number): number | null {
  if (!Number.isFinite(minutes) || minutes <= 0) return null;
  return Math.min(24 * 60, Math.round(minutes));
}

function durationText(minutes: number): string {
  if (minutes % 60 === 0) {
    const hours = minutes / 60;
    return `${hours} ${hours === 1 ? "hour" : "hours"}`;
  }
  return `${minutes} minutes`;
}

function sentenceCase(value: string): string {
  return value.charAt(0).toUpperCase() + value.slice(1);
}

function round2(value: number): number {
  return Math.round(value * 100) / 100;
}

function availabilityResult(
  availability: CompanionManualAvailability,
): CompanionAvailabilityParseResult {
  const labelLength = availability.label.trim().length;
  if (labelLength < MIN_LABEL_LENGTH) {
    return { ok: false, error: "Use at least 2 characters for free time." };
  }
  if (labelLength > MAX_LABEL_LENGTH) {
    return { ok: false, error: "Keep free time under 160 characters." };
  }
  return { ok: true, availability };
}
